'use client';

import { c as _c } from "react/compiler-runtime";
import * as SliderPrimitive from '@radix-ui/react-slider';
import { Maximize, Minimize, Pause, Play } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { SharedVideo } from '@/components/shared/shared-video';
import { cn } from '@/lib/utils';

/** Reviewers often skim a take, so the speeds sit in the bar instead of a menu. */
const PLAYBACK_RATES = [1, 1.5, 2] as const;
const SEEK_STEP_SECONDS = 5;
const ICON_BUTTON_CLASS = 'grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';
function formatTime(seconds: number): string {
  const whole = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/**
 * The demo's video with cubic's own controls in a bar under it, so they never cover the app the
 * take films: play, the position, a scrubber, speed and full screen. With focus in the player,
 * Space plays or pauses, the arrow keys seek and F toggles full screen; those keys stay out of the
 * page's own shortcuts.
 */
export function PrV2DemoPlayer(t0) {
  const $ = _c(49);
  const {
    url,
    caption
  } = t0;
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleFullscreenChange = () => setIsFullscreen(document.fullscreenElement === containerRef.current);
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const canPlay = !hasFailed && duration > 0;
  const handleTogglePlayback = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused || video.ended) {
      video.play().catch(_temp);
    } else {
      video.pause();
    }
  };
  const handleVideoClick = () => {
    containerRef.current?.focus({
      preventScroll: true
    });
    handleTogglePlayback();
  };
  const seekTo = seconds => {
    const video_0 = videoRef.current;
    if (!video_0) {
      return;
    }
    const position = Math.min(Math.max(seconds, 0), duration);
    video_0.currentTime = position;
    setCurrentTime(position);
  };
  const handleSetPlaybackRate = rate => {
    const video_1 = videoRef.current;
    if (!video_1) {
      return;
    }
    video_1.defaultPlaybackRate = rate;
    video_1.playbackRate = rate;
  };
  const handleToggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }
    if (containerRef.current?.requestFullscreen) {
      containerRef.current.requestFullscreen();
      return;
    }
    (videoRef.current as {
      webkitEnterFullscreen?: () => void;
    } | null)?.webkitEnterFullscreen?.();
  };
  const handleKeyDown = event => {
    const isOnControl = event.target instanceof Element && event.target.closest("button, [role=\"slider\"]") !== null;
    if (event.key === "f" || event.key === "F") {
      if (!event.repeat) {
        handleToggleFullscreen();
      }
    } else {
      if (isOnControl || !canPlay) {
        return;
      } else {
        if (event.key === " ") {
          if (!event.repeat) {
            handleTogglePlayback();
          }
        } else {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            seekTo(currentTime + (event.key === "ArrowLeft" ? -SEEK_STEP_SECONDS : SEEK_STEP_SECONDS));
          } else {
            return;
          }
        }
      }
    }
    event.preventDefault();
    event.stopPropagation();
  };
  const t3 = isFullscreen && "flex h-full flex-col";
  let t4;
  if ($[2] !== t3) {
    t4 = cn("border-t border-border bg-background focus-visible:outline-none", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = isFullscreen && "min-h-0 flex-1";
  let t6;
  if ($[4] !== t5) {
    t6 = cn("relative bg-black", t5);
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = isFullscreen ? "h-full" : "max-h-[28rem]";
  let t8;
  if ($[6] !== t7) {
    t8 = cn("block w-full bg-black object-contain", t7);
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setIsPlaying(true);
    t10 = () => setIsPlaying(false);
    t11 = event_0 => setCurrentTime(event_0.currentTarget.currentTime);
    t12 = event_1 => setDuration(event_1.currentTarget.duration);
    t13 = event_2 => setPlaybackRate(event_2.currentTarget.playbackRate);
    t14 = () => setHasFailed(true);
    $[8] = t10;
    $[9] = t11;
    $[10] = t12;
    $[11] = t13;
    $[12] = t14;
    $[13] = t9;
  } else {
    t10 = $[8];
    t11 = $[9];
    t12 = $[10];
    t13 = $[11];
    t14 = $[12];
    t9 = $[13];
  }
  let t15;
  if ($[14] !== hasFailed) {
    t15 = hasFailed ? <p className="absolute inset-0 grid place-items-center text-sm text-zinc-400">This video couldn’t load.</p> : null;
    $[14] = hasFailed;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  const t16 = !canPlay;
  const t17 = isPlaying ? "Pause" : "Play";
  let t18;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = cn(ICON_BUTTON_CLASS, "text-foreground");
    $[16] = t18;
  } else {
    t18 = $[16];
  }
  let t19;
  if ($[17] !== isPlaying) {
    t19 = isPlaying ? <Pause className="size-4 fill-current" aria-hidden={true} /> : <Play className="size-4 fill-current" aria-hidden={true} />;
    $[17] = isPlaying;
    $[18] = t19;
  } else {
    t19 = $[18];
  }
  let t20;
  if ($[19] !== handleTogglePlayback || $[20] !== t16 || $[21] !== t17 || $[22] !== t19) {
    t20 = <button type="button" onClick={handleTogglePlayback} disabled={t16} aria-label={t17} className={t18}>{t19}</button>;
    $[19] = handleTogglePlayback;
    $[20] = t16;
    $[21] = t17;
    $[22] = t19;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  let t21;
  if ($[24] !== currentTime) {
    t21 = formatTime(currentTime);
    $[24] = currentTime;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] !== t21) {
    t22 = <span className="shrink-0 font-mono text-xs tabular-nums text-foreground">{t21}</span>;
    $[26] = t21;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  let t23;
  if ($[28] !== currentTime) {
    t23 = [currentTime];
    $[28] = currentTime;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t26;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <SliderPrimitive.Track className="relative h-1 grow overflow-hidden rounded-full bg-foreground/15"><SliderPrimitive.Range className="absolute h-full bg-foreground" /></SliderPrimitive.Track>;
    $[30] = t26;
  } else {
    t26 = $[30];
  }
  let t27;
  if ($[31] !== currentTime) {
    t27 = formatTime(currentTime);
    $[31] = currentTime;
    $[32] = t27;
  } else {
    t27 = $[32];
  }
  let t28;
  if ($[33] !== duration) {
    t28 = formatTime(duration);
    $[33] = duration;
    $[34] = t28;
  } else {
    t28 = $[34];
  }
  const t29 = `${t27} of ${t28}`;
  let t30;
  if ($[35] !== t29) {
    t30 = <SliderPrimitive.Thumb aria-label="Seek" aria-valuetext={t29} className="block size-3 rounded-full bg-foreground opacity-0 shadow transition-opacity group-hover/seek:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />;
    $[35] = t29;
    $[36] = t30;
  } else {
    t30 = $[36];
  }
  let t31;
  if ($[37] !== duration) {
    t31 = formatTime(duration);
    $[37] = duration;
    $[38] = t31;
  } else {
    t31 = $[38];
  }
  let t32;
  if ($[39] !== t31) {
    t32 = <span className="shrink-0 font-mono text-xs tabular-nums text-zinc-500">{t31}</span>;
    $[39] = t31;
    $[40] = t32;
  } else {
    t32 = $[40];
  }
  const t33 = "group";
  const t34 = "Playback speed";
  const t35 = "flex shrink-0 rounded-md border border-border p-0.5";
  const t36 = PLAYBACK_RATES.map(rate_0 => <button key={rate_0} type="button" onClick={() => handleSetPlaybackRate(rate_0)} disabled={!canPlay} aria-pressed={playbackRate === rate_0} className="rounded px-1.5 text-[11px] tabular-nums text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 aria-pressed:bg-muted aria-pressed:text-foreground">{`${rate_0}×`}</button>);
  let t37;
  if ($[41] !== t36) {
    t37 = <div role={t33} aria-label={t34} className={t35}>{t36}</div>;
    $[41] = t36;
    $[42] = t37;
  } else {
    t37 = $[42];
  }
  const t38 = isFullscreen ? "Exit full screen" : "Full screen";
  let t39;
  if ($[43] !== isFullscreen) {
    t39 = isFullscreen ? <Minimize className="size-4" aria-hidden={true} /> : <Maximize className="size-4" aria-hidden={true} />;
    $[43] = isFullscreen;
    $[44] = t39;
  } else {
    t39 = $[44];
  }
  let t40;
  if ($[45] !== handleToggleFullscreen || $[46] !== t38 || $[47] !== t39) {
    t40 = <button type="button" onClick={handleToggleFullscreen} aria-label={t38} className={ICON_BUTTON_CLASS}>{t39}</button>;
    $[45] = handleToggleFullscreen;
    $[46] = t38;
    $[47] = t39;
    $[48] = t40;
  } else {
    t40 = $[48];
  }
  return <div ref={containerRef} tabIndex={-1} onKeyDown={handleKeyDown} className={t4}><div className={t6}><SharedVideo ref={videoRef} url={url} caption={caption} controls={false} className={t8} onClick={canPlay ? handleVideoClick : undefined} onPlay={t9} onPause={t10} onTimeUpdate={t11} onDurationChange={t12} onRateChange={t13} onUnplayable={t14} />{t15}{canPlay && !isPlaying ? <button type="button" onClick={handleTogglePlayback} aria-label="Play" className="absolute inset-0 m-auto grid size-14 place-items-center rounded-full bg-zinc-50/95 text-zinc-950 shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-50 focus-visible:ring-offset-2 focus-visible:ring-offset-black motion-reduce:transition-none"><Play className="ml-0.5 size-6 fill-current" aria-hidden={true} /></button> : null}</div><div className="flex items-center gap-2 px-3 py-1.5">{t20}{t22}<SliderPrimitive.Root value={t23} max={duration > 0 ? duration : 1} step={0.1} disabled={!canPlay} onValueChange={t24 => {
        const [t25] = t24;
        const seconds_0 = t25 === undefined ? currentTime : t25;
        return seekTo(seconds_0);
      }} className="group/seek relative flex h-5 min-w-0 flex-1 cursor-pointer touch-none select-none items-center data-[disabled]:cursor-default">{t26}{t30}</SliderPrimitive.Root>{t32}{t37}{t40}</div></div>;
}
function _temp() {}